import React, { useEffect, useState } from "react";
import { fetchDashboard } from "@/lib/api";
import { Link } from "react-router-dom";
import { Mascot } from "@/components/Mascot";
import { Wind, Thermometer, MapPin } from "lucide-react";

// Bounding box of Pyrenees (Catalonia + Andorra region)
const BBOX = { minLng: 0.4, maxLng: 2.55, minLat: 42.15, maxLat: 42.85 };

const project = (lat, lng, width, height) => {
  const x = ((lng - BBOX.minLng) / (BBOX.maxLng - BBOX.minLng)) * width;
  const y = height - ((lat - BBOX.minLat) / (BBOX.maxLat - BBOX.minLat)) * height;
  return { x, y };
};

export default function MapPage() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchDashboard().then(setData).catch(() => {});
  }, []);

  const width = 1000, height = 520;

  return (
    <div data-testid="map-page">
      <header className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.35em] text-violet-300/80 mb-2">Mapa</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Mapa de estaciones</h1>
        <p className="text-white/60 text-sm mt-2 max-w-2xl">
          Toca un marcador para revelar sus condiciones. Marcadores brillantes indican estaciones abiertas.
        </p>
      </header>

      <div className="creep-glass-strong overflow-hidden">
        <div className="relative w-full" style={{ aspectRatio: `${width}/${height}` }}>
          <svg viewBox={`0 0 ${width} ${height}`} className="absolute inset-0 w-full h-full">
            {/* Background terrain */}
            <defs>
              <radialGradient id="glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(124,58,237,0.5)" />
                <stop offset="100%" stopColor="rgba(124,58,237,0)" />
              </radialGradient>
              <linearGradient id="terrain" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#111827" />
                <stop offset="100%" stopColor="#09090B" />
              </linearGradient>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(124,58,237,0.08)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect x="0" y="0" width={width} height={height} fill="url(#terrain)" />
            <rect x="0" y="0" width={width} height={height} fill="url(#grid)" />
            {/* Mountain silhouettes */}
            <path
              d="M0,420 L80,340 L160,380 L260,300 L340,360 L440,270 L560,340 L660,260 L780,320 L880,240 L1000,320 L1000,520 L0,520 Z"
              fill="rgba(31,41,55,0.6)"
              stroke="rgba(124,58,237,0.15)"
            />
            <path
              d="M0,460 L100,410 L220,440 L340,390 L500,430 L640,380 L800,420 L1000,380 L1000,520 L0,520 Z"
              fill="rgba(76,29,149,0.15)"
            />
            {/* Region label */}
            <text x="20" y="30" fill="rgba(255,255,255,0.35)" fontSize="10" letterSpacing="4">
              PIRINEOS · 42.15° – 42.85°N · 0.40° – 2.55°E
            </text>

            {/* Andorra border approx */}
            <path
              d="M 480 90 L 560 80 L 590 110 L 590 170 L 550 200 L 490 195 L 460 160 Z"
              fill="rgba(124,58,237,0.06)"
              stroke="rgba(124,58,237,0.35)"
              strokeDasharray="4 3"
            />
            <text x="500" y="130" fill="rgba(196,181,253,0.6)" fontSize="11" letterSpacing="3">ANDORRA</text>

            {/* Stations */}
            {data?.stations?.map((s) => {
              const { x, y } = project(s.station.lat, s.station.lng, width, height);
              const available = s.available !== false;
              const isSkiable = available && s.ski?.skiable;
              const isActive = active?.station?.id === s.station.id;
              return (
                <g
                  key={s.station.id}
                  transform={`translate(${x},${y})`}
                  onClick={() => setActive(s)}
                  style={{ cursor: "pointer", opacity: available ? 1 : 0.55 }}
                  data-testid={`map-marker-${s.station.id}`}
                >
                  <circle r={isActive ? 20 : 14} fill="url(#glow)" />
                  <circle
                    r={5}
                    fill={isSkiable ? "#7C3AED" : available ? "#4B5563" : "#78716C"}
                    stroke={available ? "white" : "rgba(255,255,255,0.4)"}
                    strokeWidth={isActive ? 2 : 1}
                    strokeDasharray={available ? "0" : "2 2"}
                    style={{ filter: `drop-shadow(0 0 6px ${isSkiable ? "#7C3AED" : "#000"})` }}
                  />
                  <text
                    y={-12}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.85)"
                    fontSize="10"
                    style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.7)", strokeWidth: 3, strokeLinecap: "round" }}
                  >
                    {s.station.name}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Popup overlay */}
          {active && (
            <div
              className="absolute right-4 top-4 sm:right-6 sm:top-6 w-72 creep-glass-strong p-4 z-10"
              data-testid="map-popup"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-violet-300/80">
                    {active.station.region}
                  </div>
                  <div className="font-bold text-lg tracking-tight">{active.station.name}</div>
                </div>
                <button
                  data-testid="map-popup-close"
                  className="text-white/60 hover:text-white text-sm"
                  onClick={() => setActive(null)}
                >
                  ✕
                </button>
              </div>
              {active.available === false ? (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100">
                  <div className="uppercase tracking-widest text-[10px] text-amber-300 mb-1 font-semibold">No disponible</div>
                  {active.unavailable_reason || "Sin fuente de datos configurada."}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded-lg bg-white/[0.05] p-2">
                      <Thermometer size={12} className="text-violet-300 mb-1" />
                      <div className="font-semibold">{Math.round(active.weather.temperature)}°C</div>
                    </div>
                    <div className="rounded-lg bg-white/[0.05] p-2">
                      <Wind size={12} className="text-violet-300 mb-1" />
                      <div className="font-semibold">{Math.round(active.weather.wind_speed)}km/h</div>
                    </div>
                    <div className="rounded-lg bg-violet-500/10 p-2">
                      <div className="text-[9px] uppercase tracking-widest text-violet-300/70">Ski</div>
                      <div className="font-semibold">{active.ski?.score ?? 0}</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-white/70">
                    <div>{active.ski?.label || "—"} · {active.ski?.skiable ? "Esquiable" : "No esquiable"}</div>
                    {active.weather.freezing_level_m != null && (
                      <div>Cota de nieve: {Math.round(active.weather.freezing_level_m)} m</div>
                    )}
                    {active.ski?.reasons?.[0] && (
                      <div className="text-white/50 mt-1">{active.ski.reasons[0]}</div>
                    )}
                  </div>
                </>
              )}
              <Link
                to={`/station/${active.station.id}`}
                data-testid="map-popup-detail"
                className="creep-btn mt-4 inline-flex items-center gap-2 text-xs w-full justify-center"
              >
                Ver detalle completo
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Station list under map for accessibility */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="map-station-list">
        {data?.stations?.map((s) => (
          <button
            key={s.station.id}
            data-testid={`map-list-${s.station.id}`}
            onClick={() => setActive(s)}
            className="creep-glass p-3 text-left hover:bg-violet-500/10 transition-colors flex items-center gap-2"
          >
            <MapPin size={14} className={s.ski?.skiable ? "text-violet-300" : "text-white/40"} />
            <div className="min-w-0">
              <div className="text-sm truncate">{s.station.name}</div>
              <div className="text-[10px] text-white/50">{s.station.region}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
